import { FC, useEffect, useState } from "react";
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";

import { useWeb3ProviderContext } from "../contexts/Web3Provider";
import { AsyncTask, Badge, BadgeVerifications, ModalState } from "../domain";
import { useVerificationContext } from "../contexts/Verification";
import { PhoneVerificationModal } from "./PhoneVerificationModal";
import { fetchBadgeVerifications } from "../adapters/api";

interface BadgesTableProps {
  badges: Badge[];
  onUpdateScore: () => void;
}

export const BadgesTable: FC<BadgesTableProps> = ({
  badges,
  onUpdateScore,
}) => {
  const { connectedProvider } = useWeb3ProviderContext();
  const { verifyGitHubAccount, gitHubVerification } = useVerificationContext();
  const [phoneVerificationModal, setPhoneVerificationModal] = useState<
    ModalState<undefined>
  >({ status: "closed" });
  const [badgeVerifications, setBadgeVerifications] = useState<
    AsyncTask<BadgeVerifications, string>
  >({
    status: "pending",
  });

  const openPhoneVerificationModal = () => {
    setPhoneVerificationModal({ status: "open", data: undefined });
  };

  const onClosePhoneVerificationModal = (isCodeVerified: boolean) => {
    setPhoneVerificationModal({ status: "closed" });
    if (isCodeVerified && badgeVerifications.status === "successful") {
      setBadgeVerifications({
        status: "successful",
        data: { ...badgeVerifications.data, phoneVerified: true },
      });
      onUpdateScore();
    }
  };

  const isBadgeVerified = (badge: Badge) => {
    switch (badge.key) {
      case "github": {
        return (
          badgeVerifications.status === "successful" &&
          badgeVerifications.data.githubVerified
        );
      }
      case "phone": {
        return (
          badgeVerifications.status === "successful" &&
          badgeVerifications.data.phoneVerified
        );
      }
      default: {
        return false;
      }
    }
  };

  const isBadgeLoading = (badge: Badge) => {
    switch (badge.key) {
      case "github": {
        return (
          badgeVerifications.status === "loading" ||
          gitHubVerification.status === "loading"
        );
      }
      case "phone": {
        return badgeVerifications.status === "loading";
      }
      default: {
        return false;
      }
    }
  };

  const onVerify = (badge: Badge) => {
    switch (badge.key) {
      case "github": {
        return verifyGitHubAccount();
      }
      case "phone": {
        return openPhoneVerificationModal();
      }
      default: {
        return;
      }
    }
  };

  useEffect(() => {
    if (connectedProvider.status === "successful") {
      setBadgeVerifications({ status: "loading" });
      fetchBadgeVerifications({
        ethereumAddress: connectedProvider.data.address,
      }).then((res) =>
        setBadgeVerifications({ status: "successful", data: res })
      );
    }
  }, [connectedProvider]);

  useEffect(() => {
    if (
      gitHubVerification.status === "successful" &&
      badgeVerifications.status === "successful"
    ) {
      setBadgeVerifications({
        status: "successful",
        data: { ...badgeVerifications.data, githubVerified: true },
      });
      onUpdateScore();
    }
  }, [gitHubVerification]);

  return (
    <>
      <TableContainer bg="white" borderRadius="lg" padding="3" flex="1">
        <Table variant="simple">
          <TableCaption></TableCaption>
          <Thead>
            <Tr fontSize="2xl">
              <Th>Signals</Th>
              <Th>Points</Th>
              <Th textAlign="end">Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {badges.map((badge) => {
              return (
                <Tr key={badge.key} fontSize="lg">
                  <Td>{badge.label}</Td>
                  <Td>{badge.points}</Td>
                  <Td textAlign="end">
                    {badge.isActive ? (
                      isBadgeVerified(badge) ? (
                        <Button
                          size="lg"
                          fontSize="lg"
                          color="#11C200"
                          backgroundColor="#E7F9E5"
                          pointerEvents="none"
                        >
                          Verified
                        </Button>
                      ) : (
                        <Button
                          size="lg"
                          fontSize="lg"
                          colorScheme="primary"
                          onClick={() => onVerify(badge)}
                          isDisabled={connectedProvider.status !== "successful"}
                          isLoading={isBadgeLoading(badge)}
                        >
                          Verify
                        </Button>
                      )
                    ) : (
                      <Button size="lg" fontSize="lg" isDisabled={true}>
                        Coming soon
                      </Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
      <PhoneVerificationModal
        isOpen={phoneVerificationModal.status === "open"}
        onClose={onClosePhoneVerificationModal}
      />
    </>
  );
};
